// What the assistant accepts as attachments. Mirrors the backend
// (src/lib/aiAssistant/attachments.js): at most 4 per message, 2 of them videos.

export type AttachmentKind = 'image' | 'video';

export const MAX_ATTACHMENTS = 4;
export const MAX_VIDEO_ATTACHMENTS = 2;
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_VIDEO_BYTES = 300 * 1024 * 1024;
export const ACCEPTED_ATTACHMENT_TYPES = [
  'image/png', 'image/jpeg', 'image/webp', 'image/gif',
  'video/mp4', 'video/quicktime', 'video/webm', 'video/x-m4v',
] as const;

export type AttachmentRejection = 'unsupported_type' | 'too_large' | 'too_many';

export function attachmentKindOf(file: { type: string }): AttachmentKind | null {
  if (!(ACCEPTED_ATTACHMENT_TYPES as readonly string[]).includes(file.type)) return null;
  return file.type.startsWith('image/') ? 'image' : 'video';
}

/** Splits picked/pasted/dropped files into the ones that fit next to the
 *  current attachments and the reasons the others were left out. */
export function acceptAttachmentFiles<F extends { type: string; size: number }>(
  files: readonly F[],
  current: ReadonlyArray<{ kind: AttachmentKind }>,
): { accepted: Array<{ file: F; kind: AttachmentKind }>; rejected: AttachmentRejection[] } {
  const accepted: Array<{ file: F; kind: AttachmentKind }> = [];
  const rejected: AttachmentRejection[] = [];
  let total = current.length;
  let videos = current.filter((entry) => entry.kind === 'video').length;
  files.forEach((file) => {
    const kind = attachmentKindOf(file);
    if (!kind) {
      rejected.push('unsupported_type');
      return;
    }
    if (file.size > (kind === 'image' ? MAX_IMAGE_BYTES : MAX_VIDEO_BYTES)) {
      rejected.push('too_large');
      return;
    }
    if (total >= MAX_ATTACHMENTS || (kind === 'video' && videos >= MAX_VIDEO_ATTACHMENTS)) {
      rejected.push('too_many');
      return;
    }
    total += 1;
    if (kind === 'video') videos += 1;
    accepted.push({ file, kind });
  });
  return { accepted, rejected };
}
